import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap';
import { Dpad, RocketTakeoff, Magic } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LandingHeader from '../components/LandingHeader';
import LandingFooter from '../components/LandingFooter';
import subscriptionService from '../services/subscriptionService';
import { PricingTier } from '../types/subscription.types';
import { ENV } from '../utils/env';
import { getUtmParams } from '../utils/utmTracking';
import './Pricing.css';

const Pricing: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();

  // Redirect logged-in users to the Update Plan page
  useEffect(() => {
    if (currentUser) {
      navigate('/select-plan', { replace: true });
    }
  }, [currentUser, navigate]);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Define pricing tiers with Stripe Price IDs from environment variables
  const pricingTiers: PricingTier[] = [
    {
      name: 'starter',
      displayName: 'Starter',
      price: 0,
      priceId: '',
      interval: 'month',
      description: 'For exploring your capabilities',
      features: [
        '1 project total',
        '100 messages total',
        'Limited templates',
        '5 artifact generations total',
        '2 Figma exports total',
      ],
    },
    {
      name: 'pro',
      displayName: 'Early Adopter',
      price: 18.95,
      priceId: ENV.STRIPE_PRICE_ID_PRO || '',
      interval: 'month',
      recommended: true,
      description: 'For casual designers needing flexibility',
      features: [
        '10 projects total',
        '500 messages/month',
        'Unlimited templates',
        '50 artifact generations/month',
        '25 Figma exports/month',
        'Direct Figma plugin integration',
      ],
    },
    {
      name: 'ultra',
      displayName: 'Studio',
      price: 78.95,
      priceId: ENV.STRIPE_PRICE_ID_ULTRA || '',
      interval: 'month',
      description: 'For professionals and small teams',
      features: [
        '25 projects total',
        '1,000 messages/month',
        'Unlimited templates',
        'Unlimited artifact generations',
        'Unlimited Figma exports',
        'Early Beta feature access',
      ],
    },
  ];

  const handleSelectPlan = async (tier: PricingTier) => {
    if (!currentUser) {
      navigate('/register', { state: { selectedPlan: tier.name } });
      return;
    }

    if (tier.name === 'starter') {
      setError('You are already on the Starter plan');
      return;
    }

    try {
      setLoading(tier.name);
      setError(null);

      const successUrl = `${window.location.origin}/dashboard?checkout=success`;
      const cancelUrl = `${window.location.origin}/pricing?checkout=canceled`;

      const { url } = await subscriptionService.createCheckoutSession({
        priceId: tier.priceId,
        successUrl,
        cancelUrl,
        utmParams: getUtmParams(),
      });

      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (err: any) {
      console.error('Error creating checkout session:', err);
      setError(err.response?.data?.error || 'Failed to start checkout process');
      setLoading(null);
    }
  };

  const isCurrentPlan = (tierName: string): boolean => {
    return userProfile?.subscription?.planName === tierName;
  };

  const getIcon = (tierName: string) => {
    switch (tierName) {
      case 'starter':
        return <Dpad size={24} className="plan-icon" />;
      case 'pro':
        return <RocketTakeoff size={24} className="plan-icon" />;
      case 'ultra':
        return <Magic size={24} className="plan-icon" />;
      default:
        return <Dpad size={24} className="plan-icon" />;
    }
  };
  return (
    <div className="pricing-page">
      <LandingHeader />

      <section className="pricing-hero">
        <Container>
          <Row>
            <Col className="text-center">
              <h1 className="pricing-title mb-4">Pricing</h1>
              <p className="pricing-subtitle">A plan to suit your particular needs.</p>
            </Col>
          </Row>
        </Container>
      </section>

      {error && (
        <Container>
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        </Container>
      )}

      <section className="pricing-plans">
        <Container>
          <Row className="g-4 justify-content-center">
            {pricingTiers.map((tier) => (
              <Col key={tier.name} lg={3} md={6}>
                <Card
                  className={`pricing-card h-100 ${tier.recommended ? 'recommended' : ''} ${
                    isCurrentPlan(tier.name) ? 'current-plan' : ''
                  }`}
                >
                  <Card.Body>
                    {getIcon(tier.name)}
                    <h3 className="plan-name">{tier.displayName}</h3>
                    <div className="price-wrapper my-4">
                      {tier.name !== 'starter' && <span className="currency">$</span>}
                      <span className="price">{tier.name === 'starter' ? 'Free' : tier.price}</span>
                      {tier.name !== 'starter' && <span className="period">/{tier.interval}</span>}
                      {tier.name === 'pro' && (
                        <span className="was-price"> was <s>$49</s></span>
                      )}
                      {tier.name === 'ultra' && (
                        <span className="was-price"> was <s>$149</s></span>
                      )}
                    </div>
                    {tier.description && (
                      <p className="plan-description">{tier.description}</p>
                    )}
                    {!currentUser && (
                      <Button
                        variant={tier.recommended ? 'primary' : 'secondary'}
                        className="w-100 mt-4 plan-button"
                        onClick={() => handleSelectPlan(tier)}
                        disabled={loading !== null}
                      >
                        {loading === tier.name ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Processing...
                          </>
                        ) : tier.name === 'pro' ? (
                          'JOIN THE ONES IN MOTION'
                        ) : (
                          'Select Plan'
                        )}
                      </Button>
                    )}
                    <ul className="features-list">
                      {tier.features.map((feature, index) => (
                        <li key={index}>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <LandingFooter />
    </div>
  );
};

export default Pricing;